import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MapPin, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import {
  addMyPartnerServiceArea,
  listMyPartnerServiceAreas,
  removeMyPartnerServiceArea,
} from "@/lib/partner.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/partner/service-areas")({
  head: () => ({ meta: [{ title: "Service areas — Shields Partner" }, { name: "robots", content: "noindex" }] }),
  component: PartnerServiceAreas,
});

function PartnerServiceAreas() {
  const client = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["partner-service-areas"],
    queryFn: () => listMyPartnerServiceAreas(),
  });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [postcode, setPostcode] = useState("");
  const [radius, setRadius] = useState("15");

  async function add(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const result = await addMyPartnerServiceArea({
      data: { label, postcode, radiusMiles: Number(radius) },
    });
    setBusy(false);
    if (!result.ok) { toast.error(result.error); return; }
    toast.success("Service area added");
    setLabel(""); setPostcode(""); setRadius("15"); setOpen(false);
    await client.invalidateQueries({ queryKey: ["partner-service-areas"] });
  }

  async function remove(areaId: string) {
    const result = await removeMyPartnerServiceArea({ data: { areaId } });
    if (!result.ok) { toast.error(result.error); return; }
    await client.invalidateQueries({ queryKey: ["partner-service-areas"] });
  }

  return (
    <div className="min-h-screen bg-background pb-28">
      <AppHeader workspace="partner" />
      <main className="product-page mx-auto max-w-3xl py-8">
        <Link to="/partner/settings" className="inline-flex min-h-11 items-center gap-2 text-sm font-bold text-primary">
          <ArrowLeft className="h-4 w-4" /> Settings
        </Link>
        <header className="mt-3 rounded-[20px] border border-border bg-white p-6">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">Coverage</p>
          <h1 className="mt-1 text-2xl font-extrabold">Service areas</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your normal profile area remains your primary coverage. Enterprise can add up to five extra operating areas and matching will use the nearest eligible area.
          </p>
        </header>

        {isLoading ? <p className="mt-5 text-sm text-muted-foreground">Loading service areas…</p> : !data?.entitled ? (
          <section className="mt-5 rounded-2xl border border-border bg-white p-6">
            <MapPin className="h-7 w-7 text-primary" />
            <h2 className="mt-3 font-bold">Multiple service areas — Enterprise</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Your primary service area remains active. Extra operating areas are available with Shields Business Enterprise.
            </p>
            <Link to="/plans" className="mt-4 inline-flex min-h-11 items-center font-bold text-primary">View memberships</Link>
          </section>
        ) : (
          <>
            <div className="mt-5 flex justify-end">
              <Button onClick={() => setOpen((v) => !v)} className="min-h-11">
                <Plus className="mr-2 h-4 w-4" /> Add area
              </Button>
            </div>
            {open && (
              <form onSubmit={add} className="mt-4 grid gap-4 rounded-2xl border border-border bg-white p-5">
                <div><Label htmlFor="area-label">Area name</Label><Input id="area-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. South Manchester" required className="mt-1.5 min-h-12" /></div>
                <div><Label htmlFor="area-postcode">Centre postcode</Label><Input id="area-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} placeholder="M20 2RN" required className="mt-1.5 min-h-12" /></div>
                <div><Label htmlFor="area-radius">Working radius (miles)</Label><Input id="area-radius" type="number" min="1" max="100" value={radius} onChange={(e) => setRadius(e.target.value)} required className="mt-1.5 min-h-12" /></div>
                <Button disabled={busy}>{busy ? "Adding…" : "Add service area"}</Button>
              </form>
            )}
            <section className="mt-5 space-y-3">
              {(data?.areas ?? []).map((area: any) => (
                <article key={area.id} className="flex items-center gap-4 rounded-2xl border border-border bg-white p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><MapPin className="h-5 w-5" /></span>
                  <div className="min-w-0 flex-1"><strong>{area.label}</strong><p className="text-sm text-muted-foreground">{area.postcode} · {area.radius_miles} mile radius</p></div>
                  <button type="button" onClick={() => remove(area.id)} aria-label={"Remove " + area.label} className="rounded-lg p-2 text-destructive"><Trash2 className="h-4 w-4" /></button>
                </article>
              ))}
              {!data?.areas?.length && <p className="rounded-2xl border border-border bg-white p-5 text-sm text-muted-foreground">No extra service areas yet.</p>}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
